/**
 * LoraStack
 *
 * Multi-add list of LoRAs with per-row strength slider.
 * "+ Add LoRA" opens an inline picker; "Browse CivitAI" opens BrowseCivitAILoRAsModal.
 * Max 3 LoRAs enforced with disabled state + hint.
 * Incompatible LoRAs (base_model_compat mismatch) shown with yellow warning badge.
 *
 * Dark theme only (RULE [[CSS Architecture]]).
 * Mobile-first, 44px touch targets (RULE #13).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useClickOutside } from '@/hooks/useClickOutside';
import { type LoraInfo, listInstalledLoras } from '@/services/loraApi';
import { BrowseCivitAILoRAsModal } from './BrowseCivitAILoRAsModal';

// ============================================================================
// Types
// ============================================================================

export interface LoraEntry {
  filename: string;
  strength: number;
}

export interface LoraStackProps {
  value: LoraEntry[];
  onChange: (v: LoraEntry[]) => void;
  installedLoras: LoraInfo[];
  checkpointBaseModel: 'SD 1.5' | 'SD 1.5 Hyper' | 'SDXL 1.0';
  /** Checkpoint is already a distilled fast model (Lightning/Hyper/LCM/Turbo) —
   * Hyper-SD speed LoRAs are hidden from the picker (double distillation). */
  checkpointIsDistilled: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

const MAX_LORAS = 3;
const STRENGTH_MIN = -1.5;
const STRENGTH_MAX = 1.5;
const STRENGTH_DEFAULT = 0.65;
const STRENGTH_STEP = 0.05;

function compatModelKey(base: 'SD 1.5' | 'SD 1.5 Hyper' | 'SDXL 1.0'): 'SD 1.5' | 'SDXL 1.0' {
  return base === 'SDXL 1.0' ? 'SDXL 1.0' : 'SD 1.5';
}

function isCompatible(
  lora: LoraInfo,
  checkpointBaseModel: 'SD 1.5' | 'SD 1.5 Hyper' | 'SDXL 1.0'
): boolean {
  return lora.base_model_compat === compatModelKey(checkpointBaseModel);
}

// ============================================================================
// Sub-component: single row
// ============================================================================

function LoraRow({
  entry,
  loraInfo,
  checkpointBaseModel,
  onStrengthChange,
  onRemove,
}: {
  entry: LoraEntry;
  loraInfo: LoraInfo | undefined;
  checkpointBaseModel: 'SD 1.5' | 'SD 1.5 Hyper' | 'SDXL 1.0';
  onStrengthChange: (filename: string, strength: number) => void;
  onRemove: (filename: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const incompatible = loraInfo !== undefined && !isCompatible(loraInfo, checkpointBaseModel);
  const displayName = loraInfo?.display_name ?? entry.filename;
  const triggerWords = loraInfo?.trigger_words ?? [];

  return (
    <div
      className={`
        bg-black/30 rounded-lg border p-3 flex flex-col gap-2
        ${incompatible ? 'border-yellow-500/50' : 'border-white/10'}
      `}
    >
      {/* Top row: name + remove */}
      <div className="flex items-center gap-2">
        <span className="flex-1 text-sm text-white/90 font-medium truncate" title={entry.filename}>
          {displayName}
        </span>
        {incompatible && (
          <span className="text-[10px] bg-yellow-900/40 border border-yellow-500/30 text-yellow-300 px-1.5 py-0.5 rounded flex-shrink-0">
            {loraInfo?.base_model_compat} — {t('deforum.advanced.loraStack.incompatible')}
          </span>
        )}
        <button
          type="button"
          onClick={() => onRemove(entry.filename)}
          className="text-white/40 hover:text-red-400 transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center rounded flex-shrink-0"
          aria-label={`Remove ${displayName}`}
        >
          ✕
        </button>
      </div>

      {/* Trigger words */}
      {triggerWords.length > 0 && (
        <p className="text-[10px] text-white/40">
          {t('deforum.advanced.loraStack.triggerWords').replace(
            '{{words}}',
            triggerWords.slice(0, 4).join(', ')
          )}
        </p>
      )}

      {/* Strength slider */}
      <div className="flex items-center gap-2">
        <label className="text-xs text-white/60 flex-shrink-0 w-16">
          {t('deforum.advanced.loraStack.strengthLabel')}
        </label>
        <input
          type="range"
          min={STRENGTH_MIN}
          max={STRENGTH_MAX}
          step={STRENGTH_STEP}
          value={entry.strength}
          onChange={(e) => onStrengthChange(entry.filename, parseFloat(e.target.value))}
          className="flex-1 accent-purple-400 h-1.5"
        />
        <span className="text-xs text-white/70 w-10 text-right flex-shrink-0 tabular-nums">
          {entry.strength.toFixed(2)}
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-component: inline picker dropdown
// ============================================================================

function InlinePicker({
  options,
  alreadySelected,
  onSelect,
  onClose,
  onBrowseCivitAI,
}: {
  options: LoraInfo[];
  alreadySelected: string[];
  onSelect: (lora: LoraInfo) => void;
  onClose: () => void;
  onBrowseCivitAI: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useClickOutside(ref, onClose);

  return (
    <div
      ref={ref}
      className="absolute left-0 right-0 top-full mt-1 z-20 bg-gray-900 border border-white/10 rounded-lg shadow-xl overflow-hidden"
    >
      {options.length === 0 ? (
        <div className="px-3 py-4 text-sm text-white/40 text-center">
          No compatible LoRAs installed
        </div>
      ) : (
        <ul className="max-h-48 overflow-y-auto">
          {options.map((lora) => {
            const alreadyAdded = alreadySelected.includes(lora.filename);
            return (
              <li key={lora.filename}>
                <button
                  type="button"
                  disabled={alreadyAdded}
                  onClick={() => {
                    onSelect(lora);
                    onClose();
                  }}
                  className={`
                    w-full text-left px-3 py-2.5 text-sm transition-colors min-h-[44px]
                    ${
                      alreadyAdded
                        ? 'text-white/30 cursor-not-allowed'
                        : 'text-white/80 hover:bg-white/5 hover:text-white'
                    }
                  `}
                >
                  <span className="font-medium">{lora.display_name}</span>
                  <span className="ml-2 text-xs text-white/40">({lora.base_model_compat})</span>
                  {alreadyAdded && <span className="ml-2 text-xs text-white/30">added</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="border-t border-white/10 p-2">
        <button
          type="button"
          onClick={onBrowseCivitAI}
          className="w-full text-center text-xs text-purple-300 hover:text-purple-200 py-2 min-h-[36px] transition-colors"
        >
          {t('deforum.advanced.loraStack.browseCivitAI')}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function LoraStack({
  value,
  onChange,
  installedLoras,
  checkpointBaseModel,
  checkpointIsDistilled,
}: LoraStackProps): React.ReactElement {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [civitModalOpen, setCivitModalOpen] = useState(false);
  // Local refresh of installedLoras after CivitAI install
  const [localLoras, setLocalLoras] = useState<LoraInfo[]>(installedLoras);

  // Sync prop changes into local state
  useEffect(() => {
    setLocalLoras(installedLoras);
  }, [installedLoras]);

  // Hyper-SD speed LoRAs stacked on an already-distilled checkpoint
  // double-distill: Deforum re-feeds each frame as the next init image, so the
  // extra over-denoising compounds and the clip fries into a fixed
  // oversaturated pattern within a couple of seconds. Hide them from the
  // picker entirely rather than show a warning the user can dismiss.
  const compatibleLoras = localLoras.filter(
    (l) => isCompatible(l, checkpointBaseModel) && !(checkpointIsDistilled && l.is_hyper_speed_lora)
  );

  const handleAdd = useCallback(
    (lora: LoraInfo) => {
      if (value.length >= MAX_LORAS) return;
      if (value.some((e) => e.filename === lora.filename)) return;
      onChange([
        ...value,
        { filename: lora.filename, strength: lora.recommended_strength || STRENGTH_DEFAULT },
      ]);
    },
    [value, onChange]
  );

  const handleRemove = useCallback(
    (filename: string) => {
      onChange(value.filter((e) => e.filename !== filename));
    },
    [value, onChange]
  );

  const handleStrengthChange = useCallback(
    (filename: string, strength: number) => {
      onChange(value.map((e) => (e.filename === filename ? { ...e, strength } : e)));
    },
    [value, onChange]
  );

  const handleCivitInstalled = useCallback(
    async (filename: string, recommendedStrength: number) => {
      // Refresh installed list
      try {
        const fresh = await listInstalledLoras();
        setLocalLoras(fresh);
        // Find the newly installed lora by matching filename or job_id fallback
        const newLora = fresh.find((l) => l.filename === filename || l.is_installed);
        if (
          newLora &&
          value.length < MAX_LORAS &&
          !value.some((e) => e.filename === newLora.filename)
        ) {
          onChange([
            ...value,
            {
              filename: newLora.filename,
              strength: newLora.recommended_strength || recommendedStrength || STRENGTH_DEFAULT,
            },
          ]);
        }
      } catch {
        // If refresh fails, still close modal — user can add manually
      }
      setCivitModalOpen(false);
    },
    [value, onChange]
  );

  const maxReached = value.length >= MAX_LORAS;

  return (
    <div className="space-y-2">
      {/* Label row */}
      <div className="flex items-center justify-between">
        <label className="form-label text-white/80 font-medium">
          {t('deforum.advanced.loraStack.label')}
        </label>
        {maxReached && (
          <span className="text-xs text-white/40">
            {t('deforum.advanced.loraStack.maxReached')}
          </span>
        )}
      </div>

      {checkpointIsDistilled && (
        <p className="text-xs text-white/50">{t('deforum.advanced.loraStack.speedLorasHidden')}</p>
      )}

      {/* Existing entries */}
      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((entry) => {
            const loraInfo = localLoras.find((l) => l.filename === entry.filename);
            return (
              <LoraRow
                key={entry.filename}
                entry={entry}
                loraInfo={loraInfo}
                checkpointBaseModel={checkpointBaseModel}
                onStrengthChange={handleStrengthChange}
                onRemove={handleRemove}
              />
            );
          })}
        </div>
      )}

      {/* Add button + picker */}
      <div className="relative">
        <button
          type="button"
          disabled={maxReached}
          onClick={() => setPickerOpen((v) => !v)}
          className={`
            min-h-[44px] px-4 text-sm font-medium rounded-lg border transition-colors
            ${
              maxReached
                ? 'bg-black/20 border-white/5 text-white/30 cursor-not-allowed'
                : 'bg-black/30 border-white/10 text-white/70 hover:bg-black/40 hover:border-white/20 hover:text-white'
            }
          `}
        >
          {t('deforum.advanced.loraStack.addButton')}
        </button>

        {pickerOpen && !maxReached && (
          <InlinePicker
            options={compatibleLoras}
            alreadySelected={value.map((e) => e.filename)}
            onSelect={handleAdd}
            onClose={() => setPickerOpen(false)}
            onBrowseCivitAI={() => {
              setPickerOpen(false);
              setCivitModalOpen(true);
            }}
          />
        )}
      </div>

      {/* CivitAI modal */}
      <BrowseCivitAILoRAsModal
        isOpen={civitModalOpen}
        onClose={() => setCivitModalOpen(false)}
        onInstalled={(filename, strength) => {
          void handleCivitInstalled(filename, strength);
        }}
        baseModel={checkpointBaseModel}
      />
    </div>
  );
}

export default LoraStack;
